"use client"

import { useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useWorkflowTracking } from "@/hooks/use-workflow-tracking"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { ChevronDown, Search, MoreVertical, Save, ChevronRight, ChevronUp, Settings, Columns, Zap, LayoutList, Palette, X, RotateCcw, Mail, Bell, BellOff } from "lucide-react"
import { MessageNotificationsModal } from "@/components/message-notifications-modal"
import { useMessageNotifications } from "@/hooks/use-message-notifications"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useEntityType } from "@/contexts/entity-type-context"
import {
  useRequestsGroupState,
  selectDirtyOtherTabs,
  isRequestsGroupEntity,
  type RequestsGroupEntityType,
} from "@/contexts/requests-group-state-context"
import { useTranslation } from '@/hooks/use-translation'
import { useToast } from '@/hooks/use-toast'
import type { SavedView } from '@/types/saved-view'
import { ManageViewsModal } from './manage-views-modal'
import { AutomationModal } from './automation-modal'
import { VmsSettingsPanel } from './vms/vms-settings-panel'
import { ManageCustomFieldsModal } from './manage-custom-fields-modal'
import { env } from '@/lib/env'
import { WindowRefService } from '@/lib/window-ref-service'
import { useFeatureFlags } from '@/contexts/feature-flag-context'

/**
 * Display labels for the Requests-group entity types — used by the
 * SaveViewModal's cross-tab "also save changes on:" section. Kept in sync
 * with REQUESTS_GROUP_ENTITY_TYPES from the cross-tab context.
 *
 * Falls back to the raw entityType string when an unknown value comes
 * through; the section is informational so a missing translation is
 * recoverable.
 */
const REQUESTS_GROUP_ENTITY_LABELS: Record<string, string> = {
  ClientRequest: "Client Requests",
  VmsRequest: "VMS Requests",
  VmsEmail: "VMS Emails",
  AtsPlacementRequest: "ATS Placements",
}

// Reusable SaveViewModal component
interface AuthorizedClientItem {
  entityId: number
  name: string
}

/**
 * Sibling description for the cross-tab "also save changes on:" section.
 * Caller computes this from the shared RequestsGroupStateProvider and
 * passes it in; SaveViewModal stays presentational and doesn't reach into
 * the cross-tab store itself.
 */
interface CrossTabDirtySibling {
  /** Entity type of the dirty sibling (e.g. "VmsRequest"). */
  entityType: string
  /** Human-readable label rendered in the checkbox row (e.g. "VMS Requests"). */
  label: string
  /** Title of the view the sibling currently has loaded — shown as a hint so the user knows which view their changes will be saved into. Null when the sibling has no named view loaded. */
  viewTitle: string | null
  /**
   * Caller-computed permission gate. When false, the modal renders the row
   * with a disabled checkbox + tooltip explaining why; the user can see
   * that the sibling has changes but can't save them with their current
   * permissions. Mirrors the canSaveCurrentView rules from view-selector
   * — default views need ModifyDefaultView / admin, public non-owner
   * views need ModifyPublicViews / admin, owner can save freely. The
   * parent computes this once per render so the modal stays presentational.
   */
  canSave: boolean
  /** Optional explanation shown as tooltip when canSave=false. */
  cannotSaveReason?: string
}

interface SaveViewModalProps {
  isOpen: boolean
  onClose: () => void
  /**
   * Save callback. The optional last argument carries the entity types of
   * the sibling tabs the user opted to also save in this action. When the
   * array is empty (or undefined), this is a single-tab save and the
   * caller follows its existing flow. When non-empty, the caller should
   * dispatch one save per listed entity using each sibling's published
   * snapshot from the cross-tab store.
   */
  onSave: (
    title: string,
    isPublic: boolean,
    saveFilters: boolean,
    isClientFacing: boolean,
    authorizedClients?: AuthorizedClientItem[],
    alsoSaveEntityTypes?: string[],
    allowDataExport?: boolean,
  ) => void
  initialTitle?: string
  initialIsPublic?: boolean
  initialIsClientFacing?: boolean
  /**
   * Initial value for the per-view "Allow Data Export" toggle. Threads through
   * onSave's `allowDataExport` argument; the grid's Export button AND-gates
   * (`hasExportData && currentView.allowDataExport`). The checkbox row only
   * renders when `canExportData` is true — letting users see / set a flag
   * they can never benefit from would just add noise to the modal.
   */
  initialAllowDataExport?: boolean
  initialAuthorizedClients?: AuthorizedClientItem[]
  canManageClientViews?: boolean
  /**
   * True when the operator holds the `ExportData` authorized action. Drives
   * whether the AllowDataExport checkbox appears. Defaults false so legacy
   * callers that don't pass it get the original UI back unchanged.
   */
  canExportData?: boolean
  isSaveAs?: boolean
  isEdit?: boolean
  isCreateNew?: boolean
  /**
   * Optional: when non-empty, the modal renders a "These tabs also have
   * unsaved changes" section listing each sibling with a checkbox. Selected
   * entries are forwarded to onSave via the alsoSaveEntityTypes argument so
   * the caller can persist changes across multiple tabs in one user action.
   *
   * Empty / undefined preserves the legacy single-tab save UX exactly,
   * which is what the manage-views-modal and view-manager callers need.
   */
  crossTabDirtySiblings?: CrossTabDirtySibling[]
}

export function SaveViewModal({ isOpen, onClose, onSave, initialTitle = "", initialIsPublic = false, initialIsClientFacing = false, initialAllowDataExport = false, initialAuthorizedClients = [], canManageClientViews = false, canExportData = false, isSaveAs = false, isEdit = false, isCreateNew = false, crossTabDirtySiblings = [] }: SaveViewModalProps) {
  const { t } = useTranslation()
  const [title, setTitle] = useState(initialTitle)
  const [isPublic, setIsPublic] = useState(false)
  const [isClientFacing, setIsClientFacing] = useState(false)
  // Tracks the Allow Data Export checkbox in this modal. The grid's Export
  // button is AND-gated on this + ExportData auth code, so flipping here is
  // the canonical per-view opt-in. Defaults false on legacy views (server
  // schema default) — admins explicitly turn export on per view.
  const [allowDataExport, setAllowDataExport] = useState(false)
  const [authorizedClients, setAuthorizedClients] = useState<AuthorizedClientItem[]>([])
  // Tracks which sibling tabs the user opted to also save in this action.
  // Default-on when the modal opens: the user's mental model is "I edited
  // multiple tabs, save them all"; making them tick each box adds friction
  // for the common case. They can untick any they want to leave dirty.
  const [alsoSaveEntityTypes, setAlsoSaveEntityTypes] = useState<Set<string>>(new Set())
  const [clientSearchQuery, setClientSearchQuery] = useState('')
  const [clientSearchResults, setClientSearchResults] = useState<{ entityId: number; displayName: string; organizationCode: string }[]>([])
  const [isSearchingClients, setIsSearchingClients] = useState(false)
  const [showClientResults, setShowClientResults] = useState(false)
  const clientSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastClientSearchRef = useRef('')
  const { currentView } = useWorkflow()
  const { searchClientOrganizations } = useWorkflowApi()
  const searchClientOrgsRef = useRef(searchClientOrganizations)
  searchClientOrgsRef.current = searchClientOrganizations

  // Store initial values in refs to avoid dependency issues with Radix Dialog
  const initialValuesRef = useRef({ initialTitle, initialIsPublic, initialIsClientFacing, initialAllowDataExport, initialAuthorizedClients, isEdit, isSaveAs })
  initialValuesRef.current = { initialTitle, initialIsPublic, initialIsClientFacing, initialAllowDataExport, initialAuthorizedClients, isEdit, isSaveAs }

  // Stable ref so the on-open reset effect doesn't refire just because the
  // dirty-siblings list reference changes between renders.
  const crossTabDirtySiblingsRef = useRef(crossTabDirtySiblings)
  crossTabDirtySiblingsRef.current = crossTabDirtySiblings

  useEffect(() => {
    if (isOpen) {
      const { initialTitle: t, initialIsPublic: p, initialIsClientFacing: cf, initialAllowDataExport: ade, initialAuthorizedClients: ac, isEdit: e, isSaveAs: sa } = initialValuesRef.current
      setTitle(t)
      setIsPublic(e ? p : false)
      setIsClientFacing((e || sa) ? cf : false)
      // Seed export toggle from the loaded view when editing / "save as".
      // Plain Save (new) resets to false — admins must opt every new view in
      // explicitly so the schema-default behavior is consistent regardless of
      // which entry point created the view.
      setAllowDataExport((e || sa) ? ade : false)
      setAuthorizedClients(ac || [])
      setClientSearchQuery('')
      setClientSearchResults([])
      setShowClientResults(false)
      lastClientSearchRef.current = ''
      // Default-on every dirty sibling the user is actually allowed to save.
      // Siblings with canSave=false are shown as disabled rows in the modal
      // (so the user can SEE there are unsaved changes they can't save) but
      // start un-ticked so they don't get included in the save call.
      setAlsoSaveEntityTypes(new Set(
        crossTabDirtySiblingsRef.current
          .filter(s => s.canSave !== false)
          .map(s => s.entityType),
      ))
    }
  }, [isOpen])

  // Stable ref for authorizedClients to avoid stale closure in debounced search
  const authorizedClientsRef = useRef(authorizedClients)
  authorizedClientsRef.current = authorizedClients

  // Client org search debounce
  useEffect(() => {
    if (clientSearchTimeoutRef.current) clearTimeout(clientSearchTimeoutRef.current)
    if (clientSearchQuery.length < 2) {
      setClientSearchResults([])
      setShowClientResults(false)
      return
    }
    clientSearchTimeoutRef.current = setTimeout(async () => {
      if (clientSearchQuery === lastClientSearchRef.current) return
      lastClientSearchRef.current = clientSearchQuery
      setIsSearchingClients(true)
      try {
        const results = await searchClientOrgsRef.current(clientSearchQuery)
        const selectedIds = authorizedClientsRef.current.map(c => c.entityId)
        setClientSearchResults(results.filter(r => !selectedIds.includes(r.entityId)))
        setShowClientResults(true)
      } catch {
        setClientSearchResults([])
        setShowClientResults(false)
      } finally {
        setIsSearchingClients(false)
      }
    }, 600)
    return () => { if (clientSearchTimeoutRef.current) clearTimeout(clientSearchTimeoutRef.current) }
  }, [clientSearchQuery])

  const handleSave = (saveFilters: boolean) => {
    if (title.trim()) {
      // Forward only the entity types the user actually wants to also save.
      // Empty array (no boxes ticked, or no dirty siblings at all) means a
      // plain single-tab save and the caller follows its existing flow.
      const alsoSave = Array.from(alsoSaveEntityTypes)
      // allowDataExport sits at the tail of the signature so legacy callers
      // that destructure positional args keep working unchanged. Only emit
      // a meaningful value when the export-permission gate is on — without
      // ExportData the checkbox is hidden, and we don't want a stale piece
      // of state from a previous open to ride along.
      onSave(
        title.trim(),
        isPublic,
        saveFilters,
        isClientFacing,
        isClientFacing ? authorizedClients : undefined,
        alsoSave,
        canExportData ? allowDataExport : undefined,
      )
      onClose()
    }
  }

  const toggleAlsoSave = (entityType: string) => {
    setAlsoSaveEntityTypes(prev => {
      const next = new Set(prev)
      if (next.has(entityType)) next.delete(entityType)
      else next.add(entityType)
      return next
    })
  }

  const handleAddClient = (org: { entityId: number; displayName: string }) => {
    setAuthorizedClients(prev => [...prev, { entityId: org.entityId, name: org.displayName }])
    setClientSearchQuery('')
    setShowClientResults(false)
    lastClientSearchRef.current = ''
  }

  const handleRemoveClient = (entityId: number) => {
    setAuthorizedClients(prev => prev.filter(c => c.entityId !== entityId))
  }

  const showClientSection = canManageClientViews && isClientFacing && isPublic

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isCreateNew ? (t('createView' as any) || "Create new view") : isEdit ? "Edit view" : (isSaveAs ? (t('saveViewAs') || "Save view as") : (t('saveView') || "Save view"))}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="title" className="text-sm font-medium">
              {t('title') || "Title"}
            </label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('myCustomView') || "My custom view"}
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">
              {t('visibility') || "Visibility"}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="private"
                name="visibility"
                checked={!isPublic}
                onChange={() => { setIsPublic(false); setIsClientFacing(false); setAuthorizedClients([]) }}
                className="mr-2"
              />
              <label htmlFor="private" className="text-sm">
                {t('private') || "Private"}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="public"
                name="visibility"
                checked={isPublic}
                onChange={() => setIsPublic(true)}
                className="mr-2"
              />
              <label htmlFor="public" className="text-sm">
                {t('public') || "Public"}
              </label>
            </div>
          </div>
          {canManageClientViews && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="clientFacing"
                checked={isClientFacing && isPublic}
                disabled={!isPublic}
                onChange={(e) => {
                  setIsClientFacing(e.target.checked)
                  if (!e.target.checked) setAuthorizedClients([])
                }}
                className="mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label htmlFor="clientFacing" className={`text-sm font-medium ${!isPublic ? "opacity-50" : ""}`}>
                {t('clientFacing') || "Client facing"}
              </label>
            </div>
          )}

          {/* Allow Data Export — per-view opt-in that AND-gates with the
              ExportData authorized action on the grid Export button. Only
              rendered when the operator has ExportData (no point showing a
              flag they can't benefit from). New views default off; admins
              opt every view in explicitly. The flag fans out across
              Requests-group siblings on save so toggling here lands on
              every entity tab in the group. */}
          {canExportData && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="allowDataExport"
                checked={allowDataExport}
                onChange={(e) => setAllowDataExport(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="allowDataExport" className="text-sm font-medium">
                {t('allowDataExport' as any) || 'Allow data export'}
              </label>
            </div>
          )}

          {/* Client Organization Search — only for client-facing views */}
          <div
            className="transition-all duration-300 ease-in-out"
            style={{
              maxHeight: showClientSection ? 500 : 0,
              opacity: showClientSection ? 1 : 0,
              overflow: showClientSection ? 'visible' : 'hidden',
            }}
          >
            <div className="grid gap-2 border-t pt-3">
              <label className="text-sm font-medium">
                Client Organizations
              </label>
              <p className="text-xs text-gray-500">
                Leave empty to make visible to all clients, or select specific organizations.
              </p>

              {/* Selected clients as chips */}
              {authorizedClients.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {authorizedClients.map(client => (
                    <span
                      key={client.entityId}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-200"
                    >
                      {client.name || `Org #${client.entityId}`}
                      <button onClick={() => handleRemoveClient(client.entityId)} className="hover:text-red-500 ml-0.5" aria-label={`Remove ${client.name || 'client'}`} title={`Remove ${client.name || 'client'}`}>
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Search input */}
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <Input
                  placeholder="Search client organizations..."
                  className="pl-8 h-9 text-sm"
                  value={clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.target.value)}
                  onFocus={() => clientSearchResults.length > 0 && setShowClientResults(true)}
                />

                {/* Results dropdown */}
                {showClientResults && clientSearchResults.length > 0 && (
                  <div className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {clientSearchResults.map(org => (
                      <button
                        key={org.entityId}
                        onClick={() => handleAddClient(org)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-b-0"
                      >
                        <span className="font-medium text-gray-800">{org.displayName}</span>
                        {org.organizationCode && (
                          <span className="ml-2 text-gray-400 text-xs">({org.organizationCode})</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {isSearchingClients && (
                  <div className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                    <span className="text-xs text-gray-400">Loading...</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Cross-tab "also save changes on:" section. Only rendered when the
              caller has identified other Requests-group tabs that have unsaved
              changes — empty array means a single-tab save with the legacy
              flow. Each sibling shows its entity-type label plus the title of
              the view its changes will land in, so the user knows exactly what
              gets persisted where. */}
          {crossTabDirtySiblings.length > 0 && (
            <div className="grid gap-2 border-t pt-3">
              <label className="text-sm font-medium">
                {t('alsoSaveChangesOn') || "Other tabs with unsaved changes"}
              </label>
              <p className="text-xs text-gray-500">
                {t('alsoSaveChangesOnHelp') || "Saving will also persist changes you made on these tabs. Uncheck any you want to leave as drafts."}
              </p>
              <div className="grid gap-2">
                {crossTabDirtySiblings.map(sibling => {
                  const disabled = sibling.canSave === false
                  return (
                    <div key={sibling.entityType} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`also-save-${sibling.entityType}`}
                        checked={alsoSaveEntityTypes.has(sibling.entityType)}
                        disabled={disabled}
                        onChange={() => toggleAlsoSave(sibling.entityType)}
                        className="mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label
                        htmlFor={`also-save-${sibling.entityType}`}
                        className={`text-sm flex-1 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                        title={disabled ? sibling.cannotSaveReason : undefined}
                      >
                        <span className="font-medium">{sibling.label}</span>
                        {sibling.viewTitle && (
                          <span className="text-gray-500 ml-1">
                            ({t('updatesView' as any) || "updates"} "{sibling.viewTitle}")
                          </span>
                        )}
                        {disabled && sibling.cannotSaveReason && (
                          <span className="block text-xs text-gray-400 mt-0.5">
                            {sibling.cannotSaveReason}
                          </span>
                        )}
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="elliptical-outline" onClick={onClose}>
            {t('cancel') || "Cancel"}
          </Button>
          <Button variant="elliptical" onClick={() => handleSave(isSaveAs || isCreateNew)} disabled={!title.trim()}>
            {isCreateNew ? (t('createView' as any) || "Create") : (t('save') || "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ViewSelector({ onOpenViewManager }: { onOpenViewManager?: () => void }) {
  const { currentView, setCurrentView, saveCurrentView, saveViewAs, deleteView, hasUnsavedChanges, viewsResponse, updateViewMetadata, refreshViews: refreshViewsContext, configuration, stageFilters, globalFilters, currentUser } =
    useWorkflow()
  const workflowApi = useWorkflowApi()
  const { t } = useTranslation()
  const { toast } = useToast()
  const { trackViewAction } = useWorkflowTracking()
  const { isFeatureEnabled } = useFeatureFlags()
  // Cross-tab Requests-group state — used to surface "other tabs with
  // unsaved changes" in the SaveViewModal. Null on legacy tabs (WorkOrder
  // etc.) since they're not wrapped by RequestsGroupStateProvider; the
  // selector below returns an empty array in that case so the modal just
  // doesn't render the cross-tab section.
  const { entityType: contextEntityType } = useEntityType()
  const requestsGroupState = useRequestsGroupState()
  const isCurrentTabInGroup = isRequestsGroupEntity(contextEntityType)

  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false)
  const [isSaveAsDialogOpen, setIsSaveAsDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [newViewName, setNewViewName] = useState("")
  const [newViewIsPublic, setNewViewIsPublic] = useState(false)
  
  const searchInputRef = useRef<HTMLInputElement>(null)

  // State for the three dots menu
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false)
  const [isSaveSubmenuOpen, setIsSaveSubmenuOpen] = useState(false)
  const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false)
  const [saveViewMode, setSaveViewMode] = useState<'save' | 'saveAs' | 'edit'>('save')
  
  // State for manage views modal
  const [isManageViewsModalOpen, setIsManageViewsModalOpen] = useState(false)
  
  // State for automation modal
  const [isAutomationModalOpen, setIsAutomationModalOpen] = useState(false)

  // State for manage custom fields modal
  const [isManageCustomFieldsOpen, setIsManageCustomFieldsOpen] = useState(false)

  // State for VMS settings modal
  const [isVmsSettingsOpen, setIsVmsSettingsOpen] = useState(false)
  const [isMessageNotificationsOpen, setIsMessageNotificationsOpen] = useState(false)
  const { messageNotificationsEnabled } = useMessageNotifications()
  
  // State for unsaved changes confirmation dialog
  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState(false)
  const [pendingViewSwitch, setPendingViewSwitch] = useState<SavedView | null>(null)
  
  // State for private column warning dialog
  const [isPrivateColumnWarningOpen, setIsPrivateColumnWarningOpen] = useState(false)
  const [pendingSaveData, setPendingSaveData] = useState<{title: string, isPublic: boolean, saveFilters: boolean, isClientFacing: boolean, authorizedClients?: AuthorizedClientItem[], alsoSaveEntityTypes?: string[], allowDataExport?: boolean} | null>(null)

  // State for refresh-view confirmation dialog
  const [isRefreshConfirmOpen, setIsRefreshConfirmOpen] = useState(false)

  // State for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState<{
    myViews: boolean
    sharedWithMe: boolean
    allCompany: boolean
  }>({
    myViews: false,
    sharedWithMe: false,
    allCompany: false
  })

  // State for showing more views - track how many items to show for each section
  const [visibleCounts, setVisibleCounts] = useState<{
    myViews: number
    sharedWithMe: number
    allCompany: number
  }>({
    myViews: 3,
    sharedWithMe: 3,
    allCompany: 3
  })

  // Get the different view types from the original API response
  const defaultView = viewsResponse?.defaultView || null
  const userViews = viewsResponse?.userViews || []
  const sharedViews = viewsResponse?.sharedViews || []
  const publicViews = viewsResponse?.publicViews || []

  // Filter to only show visible views in the selector (but always show default view)
  const visibleUserViews = userViews.filter(view => view.isVisible !== false)
  const visibleSharedViews = sharedViews.filter(view => view.isVisible !== false)
  const visiblePublicViews = publicViews.filter(view => view.isVisible !== false)

  // Get authorized actions for permission checking
  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const canEditCustomColumns = authorizedActions.includes("EditColumns")
  const hasModifyDefaultViewPermission = authorizedActions.includes("ModifyDefaultView")
  const hasModifyPublicViewsPermission = authorizedActions.includes("ModifyPublicViews")
  const hasManageViewsPermission = authorizedActions.includes("ManageViews")
  const hasManageClientViewsPermission = authorizedActions.includes("ManageClientViews")
  // Same flag the grid's Export button checks. Threaded into SaveViewModal so
  // the per-view AllowDataExport checkbox only appears for operators who can
  // actually use export downstream — exposing the toggle without ExportData
  // would persist a setting that never affects the user's grid.
  const hasExportDataPermission = authorizedActions.includes("ExportData")
  const hasAutomationsLinkPermission = authorizedActions.includes("AutomationsLink")
  const hasViewVmsSettingsPermission = authorizedActions.includes("ViewVmsSettings")
  const hasAdminPermission = authorizedActions.some((action: string) => 
    action.toLowerCase().includes("admin") || action.toLowerCase().includes("administrator")
  )

  // Function to check if public view has private column filters and user should be warned
  const shouldWarnAboutPrivateColumns = (isPublic: boolean): boolean => {

    if (!isPublic || !configuration) return false
    // Get all current filters (both stage and global filters)
    const allFilters = [...stageFilters, ...globalFilters]
    if (allFilters.length === 0) return false
    
    // Get field configurations
    const fieldConfigurations = configuration.fieldsInUse || []
    
    // Check if there's at least one private column with filters applied
    const hasPrivateColumnFilter = allFilters.some((filter) => {
      const fieldConfig = fieldConfigurations.find((fc) => fc.fieldConfigurationId === filter.fieldConfigurationId)
      return fieldConfig?.isPrivate === true
    })
    if (!hasPrivateColumnFilter) return false
    
    // Check user permissions
    const authorizedActions = (configuration.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
    const hasViewPrivateColumnsPermission = authorizedActions.includes("ViewPrivateColumns")

    // If user has ViewPrivateColumns permission, always show warning
    if (hasViewPrivateColumnsPermission) return true
    
    // Otherwise, check if user has access to at least one private column that has filters
    const hasAccessToPrivateColumns = allFilters.some((filter) => {
      const fieldConfig = fieldConfigurations.find((fc) => fc.fieldConfigurationId === filter.fieldConfigurationId)
      if (!fieldConfig || !fieldConfig.isPrivate) return false
      
      // Check if current user is in the authorizedUserList
      const authorizedUsers = fieldConfig.authorizedUserList || []
      return authorizedUsers.some((user: any) => user.email === currentUser?.email)
    })
    return hasAccessToPrivateColumns
  }

  // Check if current view can be saved
  const canSaveCurrentView = currentView && configuration && (() => {
    // Check if this is the default view (either explicitly marked as isDefault or matches defaultView from API)
    const isDefaultView = currentView.isDefault === true || 
                         (defaultView?.viewSettingsId === currentView.viewSettingsId)
    
    // For default views: user must have ModifyDefaultView OR admin permission
    if (isDefaultView) {
      return hasModifyDefaultViewPermission || hasAdminPermission
    }
    
    // Check if user owns this view (in user views)
    const isOwner = userViews.some(view => view.viewSettingsId === currentView.viewSettingsId)
    if (isOwner) {
      return true
    }
    
    // Check if it's a public view and user has ModifyPublicViews permission
    const isPublicView = publicViews.some(view => view.viewSettingsId === currentView.viewSettingsId)
    if (isPublicView) {
      return hasModifyPublicViewsPermission || hasAdminPermission
    }
    
    // Check if it's a shared view (user can save if they're admin)
    const isSharedView = sharedViews.some(view => view.viewSettingsId === currentView.viewSettingsId)
    if (isSharedView) {
      return hasAdminPermission
    }
    
    // Fallback: admin can always save
    return hasAdminPermission
  })()

  // Filter views based on search term
  const filterViewsBySearch = (views: SavedView[]) => {
    if (!searchTerm.trim()) return views
    return views.filter((view) => 
      view.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  // Always show default view regardless of isVisible status
  const filteredDefaultView = defaultView ? (filterViewsBySearch([defaultView])[0] || defaultView) : null
  const filteredUserViews = filterViewsBySearch(visibleUserViews)
  const filteredSharedViews = filterViewsBySearch(visibleSharedViews)
  const filteredPublicViews = filterViewsBySearch(visiblePublicViews)

  // Get limited views for display
  const getLimitedViews = (views: SavedView[], section: 'myViews' | 'sharedWithMe' | 'allCompany') => {
    // Always show all views when searching
    if (searchTerm.trim()) {
      return views
    }
    // Show up to the visible count for this section
    return views.slice(0, visibleCounts[section])
  }

  const limitedUserViews = getLimitedViews(filteredUserViews, 'myViews')
  const limitedSharedViews = getLimitedViews(filteredSharedViews, 'sharedWithMe')
  const limitedPublicViews = getLimitedViews(filteredPublicViews, 'allCompany')

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  // Handle view switching with unsaved changes confirmation
  const handleViewSwitch = async (view: SavedView) => {
    if (currentView?.viewSettingsId !== view.viewSettingsId) {
      // Check if user has unsaved changes and can modify current view and has ManageViews permission
      if (hasUnsavedChanges && canSaveCurrentView && hasManageViewsPermission) {
        // Show confirmation dialog
        setPendingViewSwitch(view)
        setIsUnsavedChangesDialogOpen(true)
      } else {
        // Switch view immediately
        trackViewAction('load', view.title)
        await setCurrentView(view)
        setIsViewDropdownOpen(false)
      }
    }
  }

  // Handle confirmation to switch view and lose unsaved changes
  const handleConfirmViewSwitch = async () => {
    if (pendingViewSwitch) {
      trackViewAction('load', pendingViewSwitch.title)
      await setCurrentView(pendingViewSwitch)
      setIsViewDropdownOpen(false)
      setIsUnsavedChangesDialogOpen(false)
      setPendingViewSwitch(null)
    }
  }

  // Handle cancel view switch
  const handleCancelViewSwitch = () => {
    setIsUnsavedChangesDialogOpen(false)
    setPendingViewSwitch(null)
  }

  // Refresh the current view — re-applies the saved view state, discarding any
  // unsaved filter/sort changes. If the user has unsaved changes, warn first.
  const handleRefreshView = async () => {
    if (!currentView) return
    if (hasUnsavedChanges) {
      setIsRefreshConfirmOpen(true)
      return
    }
    await setCurrentView(currentView)
  }

  const handleConfirmRefresh = async () => {
    if (currentView) {
      await setCurrentView(currentView)
    }
    setIsRefreshConfirmOpen(false)
  }

  // Maintain focus on search input when dropdown is open
  useEffect(() => {
    if (isViewDropdownOpen && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [isViewDropdownOpen])

  // Reset show more state when dropdown closes
  useEffect(() => {
    if (!isViewDropdownOpen) {
      setVisibleCounts({
        myViews: 3,
        sharedWithMe: 3,
        allCompany: 3
      })
    }
  }, [isViewDropdownOpen])

  // Cleanup pointer-events when all dialogs/menus are closed
  useEffect(() => {
    const allClosed = !isActionsMenuOpen && !isSaveSubmenuOpen &&
                     !isSaveViewModalOpen && !isPrivateColumnWarningOpen &&
                     !isDeleteDialogOpen && !isManageViewsModalOpen &&
                     !isAutomationModalOpen && !isUnsavedChangesDialogOpen &&
                     !isManageCustomFieldsOpen && !isVmsSettingsOpen
    
    if (allClosed) {
      // Ensure body pointer-events are reset
      const timer = setTimeout(() => {
        if (document.body.style.pointerEvents === 'none') {
          document.body.style.pointerEvents = ''
        }
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [isActionsMenuOpen, isSaveSubmenuOpen, isSaveViewModalOpen,
      isPrivateColumnWarningOpen, isDeleteDialogOpen, isManageViewsModalOpen,
      isAutomationModalOpen, isUnsavedChangesDialogOpen, isManageCustomFieldsOpen,
      isVmsSettingsOpen])

  // Toggle section collapse
  const toggleSection = (section: 'myViews' | 'sharedWithMe' | 'allCompany') => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  // Handle save as (original function)
  const handleSaveAsOriginal = async () => {
    if (newViewName.trim()) {
      try {
        await saveViewAs(newViewName.trim(), newViewIsPublic)
        setNewViewName("")
        setNewViewIsPublic(false)
        setIsSaveAsDialogOpen(false)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: t('saveView') || "View saved successfully",
        })
      } catch (error) {
        toast({
          variant: "destructive",
          title: t('error') || "Error",
          description: error instanceof Error ? error.message : "Failed to save view",
        })
      }
    }
  }

  // Handle delete (original function)
  const handleDeleteOriginal = async () => {
    if (currentView) {
      try {
        await deleteView(currentView.viewSettingsId)
        setIsDeleteDialogOpen(false)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: t('deleteView') || "View deleted successfully",
        })
      } catch (error) {
        toast({
          variant: "destructive",
          title: t('error') || "Error",
          description: error instanceof Error ? error.message : "Failed to delete view",
        })
      }
    }
  }

  const handleSave = async () => {
    // Close menus first
    setIsActionsMenuOpen(false)
    setIsSaveSubmenuOpen(false)
    
    // Check if we need to show private column warning for public views
    const isCurrentViewPublic = currentView?.isPublic === true
    if (shouldWarnAboutPrivateColumns(isCurrentViewPublic)) {
      setPendingSaveData({ title: currentView?.title || '', isPublic: isCurrentViewPublic, saveFilters: false, isClientFacing: currentView?.isClientFacing ?? false })
      // Delay opening dialog to allow dropdown cleanup
      setTimeout(() => {
        setIsPrivateColumnWarningOpen(true)
      }, 100)
      return
    }

    // No warning needed, proceed with save
    await handleSaveInternal()
  }

  const handleSaveInternal = async () => {
    try {
      await saveCurrentView()
      trackViewAction('save', currentView?.title || 'Custom View')
      toast({
        variant: "success",
        title: t('success') || "Success",
        description: t('changesSuccess') || "Changes saved successfully",
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: error instanceof Error ? error.message : "Failed to save changes",
      })
    }
  }

  const handleEdit = () => {
    trackViewAction('edit', currentView?.title || 'Custom View')
    setIsActionsMenuOpen(false)
    // Delay opening modal to allow dropdown cleanup
    setTimeout(() => {
      setIsManageViewsModalOpen(true)
    }, 100)
  }

  const handleSaveAs = () => {
    trackViewAction('create', 'New View')
    setSaveViewMode('saveAs')
    // Close menus first
    setIsActionsMenuOpen(false)
    setIsSaveSubmenuOpen(false)
    // Delay opening modal to allow dropdown cleanup
    setTimeout(() => {
      setIsSaveViewModalOpen(true)
    }, 100)
  }

  const handleDelete = () => {
    trackViewAction('delete', currentView?.title || 'Custom View')
    setIsActionsMenuOpen(false)
    // Delay opening dialog to allow dropdown cleanup
    setTimeout(() => {
      setIsDeleteDialogOpen(true)
    }, 100)
  }

  const handleSaveViewModalSave = async (title: string, isPublic: boolean, saveFilters: boolean, isClientFacing: boolean, authorizedClients?: AuthorizedClientItem[], alsoSaveEntityTypes?: string[], allowDataExport?: boolean) => {
    // Check if we need to show private column warning
    if (shouldWarnAboutPrivateColumns(isPublic)) {
      setPendingSaveData({ title, isPublic, saveFilters, isClientFacing, authorizedClients, alsoSaveEntityTypes, allowDataExport })
      setIsSaveViewModalOpen(false)
      // Delay opening dialog to allow previous dialog cleanup
      setTimeout(() => {
        setIsPrivateColumnWarningOpen(true)
      }, 100)
      return
    }

    // No warning needed, proceed with save
    await handleSaveViewModalSaveInternal(title, isPublic, saveFilters, isClientFacing, authorizedClients, alsoSaveEntityTypes, allowDataExport)
  }

  /**
   * Resolve whether the current user can save a given sibling's currently-
   * loaded view. Mirrors the canSaveCurrentView gate from this file
   * (line ~567) — default views need ModifyDefaultView / admin, public
   * non-owner views need ModifyPublicViews / admin, owner views always
   * pass, shared views need admin. Returns either `{ canSave: true }` or
   * `{ canSave: false, reason: <human-readable> }` so the modal can
   * surface the why alongside the disabled checkbox.
   */
  const resolveSiblingCanSave = (
    isDefault: boolean,
    isPublic: boolean,
    isOwner: boolean,
  ): { canSave: boolean; reason?: string } => {
    if (isDefault) {
      return (hasModifyDefaultViewPermission || hasAdminPermission)
        ? { canSave: true }
        : { canSave: false, reason: "You don't have permission to modify the default view" }
    }
    if (isOwner) return { canSave: true }
    if (isPublic) {
      return (hasModifyPublicViewsPermission || hasAdminPermission)
        ? { canSave: true }
        : { canSave: false, reason: "You don't have permission to modify public views" }
    }
    // Shared (or unknown category) — admin only. Mirrors the canSaveCurrentView
    // fallthrough for sharedViews + the final admin-only catch-all.
    return hasAdminPermission
      ? { canSave: true }
      : { canSave: false, reason: "You don't have permission to modify this view" }
  }

  /**
   * Persist a sibling tab's pending changes to its own ViewSettings doc.
   * Called once per ticked sibling from the cross-tab save flow. Uses the
   * sibling's published snapshot (currentViewSettingsId + the latest stage
   * configs/filters captured by its WorkflowProvider into the cross-tab
   * store). isUserViewStateUpdate is intentionally false — we're updating
   * the shared ViewSettings doc, not the active tab's per-user state.
   * The sibling's own UserViewState (if any) keeps pointing at the same
   * ViewSettingsId and will see the updated content on its next mount.
   *
   * Defensive permission check: even though the modal disables the
   * checkbox when canSave is false, we re-check here so a tick that
   * survived an out-of-band state change (e.g. permissions revoked
   * between modal open and save click) doesn't slip through.
   */
  const saveSiblingFromSnapshot = async (siblingEntityType: RequestsGroupEntityType) => {
    if (!requestsGroupState) return
    const snapshot = requestsGroupState.tabs[siblingEntityType]
    if (!snapshot || !snapshot.currentViewSettingsId) return

    const gate = resolveSiblingCanSave(
      snapshot.currentViewIsDefault,
      snapshot.currentViewIsPublic,
      snapshot.currentViewIsOwner,
    )
    if (!gate.canSave) {
      throw new Error(gate.reason || `Cannot modify ${siblingEntityType} sibling view`)
    }

    // Serialize stage-filter values to JSON strings the same way
    // saveCurrentView does — backend expects values: string, not values: any.
    const serializedStageFilters = snapshot.stageFilters.map(filter => ({
      ...filter,
      values: filter.values ? (typeof filter.values === 'string' ? filter.values : JSON.stringify(filter.values)) : undefined,
    }))

    await workflowApi.saveViewSettings({
      id: snapshot.currentViewSettingsId,
      title: snapshot.currentViewTitle || '',
      viewGroupCode: snapshot.currentViewGroupCode ?? undefined,
      stageConfigurations: snapshot.stageConfigurations,
      stageFilters: serializedStageFilters,
      globalFilters: snapshot.globalFilters,
      isUserViewStateUpdate: false,
      updateFilters: true,
      entityType: siblingEntityType,
    })
  }

  const handleSaveViewModalSaveInternal = async (title: string, isPublic: boolean, saveFilters: boolean, isClientFacing: boolean, authorizedClients?: AuthorizedClientItem[], alsoSaveEntityTypes?: string[], allowDataExport?: boolean) => {
    const visibleInApplications = isClientFacing ? ['Client'] : []
    // Cross-tab sibling saves run AFTER the active tab's save lands so any
    // failure on a sibling doesn't roll back the user's primary action. Each
    // sibling save is independent — a failure on VMS doesn't abort the
    // ClientRequest sibling. Filter to entries that actually have a snapshot
    // in the cross-tab store (i.e. the sibling tab was mounted at least once
    // this session).
    const siblingEntityTypes = (alsoSaveEntityTypes || []).filter(
      (et): et is RequestsGroupEntityType => isRequestsGroupEntity(et) && et !== contextEntityType,
    )
    try {
      if (saveViewMode === 'save') {
        await saveCurrentView()
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: t('changesSuccess') || "Changes saved successfully",
        })
      } else if (saveViewMode === 'edit') {
        // Single API call with authorizedClientList included.
        //
        // allowDataExport: send the current view's value when the modal's toggle is hidden (user lacks
        // ExportData) so an edit through this path never changes it implicitly.
        //
        // NOTE: this is now belt-and-braces, not the load-bearing guard it used to be. AllowDataExport
        // and IsPublic are `bool?` on SaveViewOrStateSettingsRequestDto / SaveViewGroupRequestDto, and
        // the BE only writes them when `.HasValue` — so omitting them means "leave as-is" rather than
        // deserializing to the C# default `false` and clobbering the stored value. Keep the fallback
        // (explicit intent is clearer than relying on omission), but don't reinstate this reasoning as
        // a constraint elsewhere.
        await workflowApi.saveViewSettings({
          id: currentView?.viewSettingsId,
          title,
          isPublic,
          allowDataExport: allowDataExport ?? (currentView?.allowDataExport ?? false),
          isTitleAndVisibilityUpdate: true,
          isUserViewStateUpdate: false,
          updateFilters: false,
          stageConfigurations: [],
          stageFilters: [],
          globalFilters: [],
          visibleInApplications,
          authorizedClientList: authorizedClients,
        })
        // Refresh views context to get updated data including authorizedClientList
        await refreshViewsContext()
        trackViewAction('edit', title)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: "View updated successfully",
        })
      } else {
        const newView = await saveViewAs(title, isPublic, visibleInApplications, allowDataExport ?? false)
        if (newView) {
          await setCurrentView(newView)
        }
        trackViewAction('create', title)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: t('saveViewAs') || "View saved as new successfully",
        })
      }

      // After the primary save lands, persist each ticked cross-tab sibling.
      // Failures here surface as separate toasts but don't roll back the
      // primary action — partial success is preferable to losing the user's
      // current tab edits because a sibling save threw.
      if (siblingEntityTypes.length > 0) {
        const failures: string[] = []
        for (const siblingEntityType of siblingEntityTypes) {
          try {
            await saveSiblingFromSnapshot(siblingEntityType)
          } catch (err) {
            failures.push(siblingEntityType)
            console.error(`Failed to save sibling ${siblingEntityType}:`, err)
          }
        }
        if (failures.length > 0) {
          toast({
            variant: "destructive",
            title: t('error') || "Error",
            description: `Could not save changes on: ${failures.join(', ')}`,
          })
        } else {
          toast({
            variant: "success",
            title: t('success') || "Success",
            description: `Also saved changes on ${siblingEntityTypes.length} other tab${siblingEntityTypes.length === 1 ? '' : 's'}`,
          })
        }
      }

      setIsSaveViewModalOpen(false)
    } catch (error) {
      console.error('Failed to save view:', error)
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: error instanceof Error ? error.message : "Failed to save view",
      })
    }
  }

  return (
    <div className="flex items-center gap-2">
      {/* View Selector Dropdown */}
      <div className="relative">
        <TooltipProvider>
          <DropdownMenu open={isViewDropdownOpen} onOpenChange={setIsViewDropdownOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 min-w-[200px] justify-between">
                    <span className="truncate">{currentView?.title || "Default"}</span>
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('viewSelector') || "View selector"}</p>
              </TooltipContent>
            </Tooltip>
          <DropdownMenuContent className="w-[280px]" align="start">
            <div className="p-2">
              <div className="relative mb-2">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  ref={searchInputRef}
                  placeholder={t('search') || "Search"}
                  className="pl-8"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Default view - no section title */}
            {filteredDefaultView && (
              <DropdownMenuItem
                onClick={() => handleViewSwitch(filteredDefaultView)}
                className={currentView?.viewSettingsId === filteredDefaultView.viewSettingsId ? "bg-gray-100" : ""}
              >
                {filteredDefaultView.title}
              </DropdownMenuItem>
            )}

            {/* My views section */}
            <DropdownMenuSeparator />
            <div 
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleSection('myViews')
              }}
              className="flex items-center justify-between px-2 py-1.5 text-xs text-gray-500 cursor-pointer hover:bg-gray-50"
            >
              <span>{t('myViews') || "My views"}</span>
              {collapsedSections.myViews ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </div>
            <div className={`overflow-hidden transition-all duration-200 ease-in-out ${collapsedSections.myViews ? 'max-h-0' : 'max-h-96'}`}>
              {limitedUserViews.map((view) => (
                <DropdownMenuItem
                  key={view.viewSettingsId}
                  onClick={() => handleViewSwitch(view)}
                  className={currentView?.viewSettingsId === view.viewSettingsId ? "bg-gray-100" : ""}
                >
                  {view.title}
                </DropdownMenuItem>
              ))}
              {filteredUserViews.length > visibleCounts.myViews && !searchTerm.trim() && (
                <div className="px-2 py-1.5">
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setVisibleCounts(prev => {
                        const newCount = Math.min(prev.myViews + 5, filteredUserViews.length)
                        return { ...prev, myViews: newCount }
                      })
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 w-full text-left"
                  >
                    {t('showMore') || "Show more"} ({filteredUserViews.length - visibleCounts.myViews})
                  </button>
                </div>
              )}
            </div>



            {/* All company section */}
            <DropdownMenuSeparator />
            <div 
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleSection('allCompany')
              }}
              className="flex items-center justify-between px-2 py-1.5 text-xs text-gray-500 cursor-pointer hover:bg-gray-50"
            >
              <span>{t('allCompany') || "All company"}</span>
              {collapsedSections.allCompany ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </div>
            <div className={`overflow-hidden transition-all duration-200 ease-in-out ${collapsedSections.allCompany ? 'max-h-0' : 'max-h-96'}`}>
              {limitedPublicViews.map((view) => (
                <DropdownMenuItem
                  key={view.viewSettingsId}
                  onClick={() => handleViewSwitch(view)}
                  className={currentView?.viewSettingsId === view.viewSettingsId ? "bg-gray-100" : ""}
                >
                  {view.title}
                </DropdownMenuItem>
              ))}
              {filteredPublicViews.length > visibleCounts.allCompany && !searchTerm.trim() && (
                <div className="px-2 py-1.5">
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setVisibleCounts(prev => {
                        const newCount = Math.min(prev.allCompany + 5, filteredPublicViews.length)
                        return { ...prev, allCompany: newCount }
                      })
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 w-full text-left"
                  >
                    {t('showMore') || "Show more"} ({filteredPublicViews.length - visibleCounts.allCompany})
                  </button>
                </div>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        </TooltipProvider>
      </div>

      {/* More Options Button */}
      <DropdownMenu open={isActionsMenuOpen} onOpenChange={setIsActionsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVertical size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuItem
            onClick={() => {
              setIsActionsMenuOpen(false)
              // Delay so the dropdown can close cleanly before the confirm dialog opens
              setTimeout(() => handleRefreshView(), 100)
            }}
            disabled={!currentView}
            className="pl-2 pr-2"
          >
            <RotateCcw size={16} className="mr-2" />
            {t('refreshView')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {hasManageViewsPermission && (
            <DropdownMenu open={isSaveSubmenuOpen} onOpenChange={setIsSaveSubmenuOpen}>
              <DropdownMenuTrigger asChild>
                <DropdownMenuItem 
                  onSelect={(e) => e.preventDefault()}
                  className="pl-2 pr-2"
                >
                  <Columns size={16} className="mr-2" />
                  {t('saveThisView') || "Save this view"}
                  <ChevronRight size={16} className="ml-auto" />
                </DropdownMenuItem>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" className="w-48" align="start">
                <DropdownMenuItem 
                  onClick={handleSave}
                  disabled={!canSaveCurrentView}
                  className="pl-2"
                >
                  <Save size={16} className="mr-2" />
                  {t('saveChanges') || "Save changes"}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleSaveAs}
                  className="pl-2"
                >
                  <Save size={16} className="mr-2" />
                  {t('saveAsNew') || "Save as new"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenuItem onClick={handleEdit} className="pl-2 pr-2">
            <Settings size={16} className="mr-2" />
            {"Manage views"}
          </DropdownMenuItem>
          {onOpenViewManager && isFeatureEnabled('viewDesigner') && hasManageViewsPermission && (
            <DropdownMenuItem
              onClick={() => {
                setIsActionsMenuOpen(false)
                onOpenViewManager()
              }}
              className="pl-2 pr-2"
            >
              <Palette size={16} className="mr-2" />
              {"View Designer"}
            </DropdownMenuItem>
          )}
          {canEditCustomColumns && (
            <DropdownMenuItem
              onClick={() => {
                setIsActionsMenuOpen(false)
                setTimeout(() => setIsManageCustomFieldsOpen(true), 100)
              }}
              className="pl-2 pr-2"
            >
              <LayoutList size={16} className="mr-2" />
              {t("manageCustomFields") || "Manage custom fields"}
            </DropdownMenuItem>
          )}
          {isFeatureEnabled('automationsLinkEnabled') && hasAutomationsLinkPermission && (
            <DropdownMenuItem
              onClick={() => {
                setIsActionsMenuOpen(false)
                // Delay opening modal to allow dropdown cleanup
                setTimeout(() => {
                  setIsAutomationModalOpen(true)
                }, 100)
              }}
              className="pl-2 pr-2"
            >
              <Zap size={16} className="mr-2" />
              {t('automations') || "Automations"}
            </DropdownMenuItem>
          )}
          { isFeatureEnabled('vmsIntegration') && hasViewVmsSettingsPermission && (
            <DropdownMenuItem
              onClick={() => {
                setIsActionsMenuOpen(false)
                setTimeout(() => {
                  setIsVmsSettingsOpen(true)
                }, 100)
              }}
              className="pl-2 pr-2"
            >
              <Mail size={16} className="mr-2" />
              {"VMS Settings"}
            </DropdownMenuItem>
          )}
          {isFeatureEnabled('messages') && (
            <DropdownMenuItem
              onClick={() => {
                setIsActionsMenuOpen(false)
                setTimeout(() => setIsMessageNotificationsOpen(true), 100)
              }}
              className="pl-2 pr-2"
            >
              {messageNotificationsEnabled ? <Bell size={16} className="mr-2" /> : <BellOff size={16} className="mr-2" />}
              <span className="flex-1">{t('messageNotifications') || 'Message notifications'}</span>
              {/* State shown as a word as well as the icon: an icon alone leaves people guessing
                  whether it depicts the current state or the action. */}
              <span className="ml-3 text-gray-500">
                {messageNotificationsEnabled ? (t('on') || 'On') : (t('off') || 'Off')}
              </span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <MessageNotificationsModal
        open={isMessageNotificationsOpen}
        onOpenChange={setIsMessageNotificationsOpen}
      />

      {/* Save View Modal — passes cross-tab dirty siblings so the modal can
          surface a "These tabs also have unsaved changes" section with
          checkboxes. Computed on every render (cheap — at most 3 entries to
          scan); only non-empty when (a) the current tab is a Requests-group
          entity AND (b) other group tabs have published unsaved changes
          this session. saveAs / edit flows opt out of the cross-tab UX —
          those are inherently single-doc operations; cross-tab propagation
          only makes sense for the 'save in place' path. */}
      <SaveViewModal
        isOpen={isSaveViewModalOpen}
        onClose={() => setIsSaveViewModalOpen(false)}
        onSave={handleSaveViewModalSave}
        initialTitle={saveViewMode === 'saveAs' && currentView?.title ? `${currentView.title} - Copy` : (currentView?.title || "")}
        initialIsPublic={currentView?.isPublic ?? false}
        initialIsClientFacing={currentView?.isClientFacing ?? false}
        initialAllowDataExport={currentView?.allowDataExport ?? false}
        initialAuthorizedClients={(currentView?.authorizedClientList || []).map(c => ({ entityId: c.entityId, name: c.name }))}
        canManageClientViews={hasManageClientViewsPermission}
        canExportData={hasExportDataPermission}
        isSaveAs={saveViewMode === 'saveAs'}
        isEdit={saveViewMode === 'edit'}
        crossTabDirtySiblings={
          saveViewMode === 'save' && isCurrentTabInGroup && requestsGroupState
            ? selectDirtyOtherTabs(requestsGroupState, contextEntityType as RequestsGroupEntityType).map(({ entityType, snapshot }) => {
                const gate = resolveSiblingCanSave(
                  snapshot.currentViewIsDefault,
                  snapshot.currentViewIsPublic,
                  snapshot.currentViewIsOwner,
                )
                return {
                  entityType,
                  label: REQUESTS_GROUP_ENTITY_LABELS[entityType] || entityType,
                  viewTitle: snapshot.currentViewTitle,
                  canSave: gate.canSave,
                  cannotSaveReason: gate.reason,
                }
              })
            : []
        }
      />

      {/* Save As Dialog */}
      <Dialog open={isSaveAsDialogOpen} onOpenChange={setIsSaveAsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('saveViewAs') || "Save view as"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="name" className="text-sm font-medium">
                {t('title') || "Title"}
              </label>
              <Input
                id="name"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder={t('myCustomView') || "My custom view"}
              />
            </div>
            
            <div className="grid gap-2">
              <label className="text-sm font-medium">
                {t('visibility') || "Visibility"}
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="old-private"
                  name="old-visibility"
                  checked={!newViewIsPublic}
                  onChange={() => setNewViewIsPublic(false)}
                  className="mr-2"
                />
                <label htmlFor="old-private" className="text-sm">
                  {t('private') || "Private"}
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="old-public"
                  name="old-visibility"
                  checked={newViewIsPublic}
                  onChange={() => setNewViewIsPublic(true)}
                  className="mr-2"
                />
                <label htmlFor="old-public" className="text-sm">
                  {t('public') || "Public"}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="elliptical-outline" onClick={() => setIsSaveAsDialogOpen(false)}>
              {t('cancel') || "Cancel"}
            </Button>
            <Button variant="elliptical" onClick={handleSaveAsOriginal} disabled={!newViewName.trim()}>
              {t('save') || "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteViewConfirmation') || "Are you sure you want to delete this view?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteViewWarning') || "This view will be deleted for everyone it has been shared with. You cannot undo this action."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel') || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOriginal} className="bg-red-600 hover:bg-red-700">
              {t('delete') || "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Views Modal */}
      <ManageViewsModal
        isOpen={isManageViewsModalOpen}
        onClose={() => setIsManageViewsModalOpen(false)}
      />

      {/* Automation Modal — seeded with the active tab's entityType so the
          modal opens directly on the matching rule list. Without this seed
          the modal falls back to WorkOrder regardless of which Request tab
          the user fired the kebab from (root cause of the "Client Request
          → Automations always shows Work Order rules" bug). contextEntityType
          comes from the surrounding EntityTypeProvider so it correctly
          tracks VMS sub-tabs (VmsRequest vs VmsEmail) — each sub-tab mounts
          its own EntityTypeProvider with the matching entity. */}
      <AutomationModal
        isOpen={isAutomationModalOpen}
        onClose={() => setIsAutomationModalOpen(false)}
        initialEntityType={contextEntityType}
      />

      {/* Manage Custom Fields Modal */}
      <ManageCustomFieldsModal
        isOpen={isManageCustomFieldsOpen}
        onClose={() => setIsManageCustomFieldsOpen(false)}
      />

      {/* VMS Settings Panel (full-screen) */}
      {isVmsSettingsOpen && (
        <VmsSettingsPanel onClose={() => setIsVmsSettingsOpen(false)} />
      )}

      {/* Unsaved Changes Confirmation Dialog */}
      <AlertDialog open={isUnsavedChangesDialogOpen} onOpenChange={setIsUnsavedChangesDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('unsavedChangesTitle') || "Unsaved Changes"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('unsavedChangesWarning') || "You have unsaved changes to your filters. Switching views will discard these changes. Are you sure you want to continue?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelViewSwitch} variant="elliptical-outline">
              {t('cancel') || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmViewSwitch} variant="elliptical">
              {t('switchView') || "Switch View"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Refresh View Confirmation Dialog */}
      <AlertDialog open={isRefreshConfirmOpen} onOpenChange={setIsRefreshConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('refreshViewTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('refreshViewMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="elliptical-outline">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRefresh} variant="elliptical">
              {t('refreshView')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Private Column Warning Dialog */}
      <AlertDialog open={isPrivateColumnWarningOpen} onOpenChange={setIsPrivateColumnWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('privateColumnWarningTitle') || "Private Column Filters Warning"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('privateColumnWarningMessage') || "This public view contains filters on private columns. Other users who don't have access to these private columns may not see the same results or may encounter errors when using this view. Are you sure you want to save this public view?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setIsPrivateColumnWarningOpen(false)
                setPendingSaveData(null)
              }} 
              variant="elliptical-outline"
            >
              {t('cancel') || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                if (pendingSaveData) {
                  // Check if this is a modal save (has title different from current view) or a direct save
                  if (pendingSaveData.title !== currentView?.title || pendingSaveData.saveFilters) {
                    // Modal save - use the modal save handler
                    await handleSaveViewModalSaveInternal(pendingSaveData.title, pendingSaveData.isPublic, pendingSaveData.saveFilters, pendingSaveData.isClientFacing, pendingSaveData.authorizedClients, pendingSaveData.alsoSaveEntityTypes, pendingSaveData.allowDataExport)
                  } else {
                    // Direct save - use the internal save handler
                    await handleSaveInternal()
                  }
                }
                setIsPrivateColumnWarningOpen(false)
                setPendingSaveData(null)
              }} 
              variant="elliptical"
            >
              {t('saveAnyway') || "Save Anyway"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}